import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface DeclaracaoTacDialogData {
  dataConsulta: Date;
  cpf: string;
  nome: string;
  situacao: 'nada_consta' | 'possui_pendencias';
  servidorId?: number;
  podeEmitir: boolean;
  mensagemPendencias?: string;
  emissaoCompleta?: boolean;
  tacs?: TacImpeditivo[];
}

export interface TacImpeditivo {
  id: number;
  numero_processo_sei: string;
  data_registro_tac?: string | null;
  data_inicio_vigencia_tac: string;
  suspensao: boolean;
  status: string;
  orgao: { nome: string };
}

export interface TacUnificado {
  numero: string;
  status: string;
  dataCelebracao: string;
  dataVigencia: string;
}

@Component({
  selector: 'app-declaracao-tac-dialog',
  templateUrl: './declaracao-tac-dialog.component.html',
  styleUrls: ['./declaracao-tac-dialog.component.css']
})
export class DeclaracaoTacDialogComponent implements OnInit {
  tacsUnificados: TacUnificado[] = [];
  tacsSelecionados = new Set<string>();
  observacoes = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DeclaracaoTacDialogData,
    private dialogRef: MatDialogRef<DeclaracaoTacDialogComponent>
  ) {}

  get situacaoLabel(): string {
    return this.data.situacao === 'nada_consta' ? 'Nada Consta' : 'Possui Pendências';
  }

  get dataConsultaFormatada(): string {
    const d = this.data.dataConsulta;
    return d ? new Date(d).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) : '';
  }

  get consultadoLabel(): string {
    return `${this.data.cpf || ''} - ${this.data.nome || ''}`.trim();
  }

  get emissaoCompleta(): boolean {
    return !!this.data.emissaoCompleta;
  }

  get showProcessTable(): boolean {
    return this.data.situacao === 'possui_pendencias' && this.tacsUnificados.length > 0;
  }

  get showSelecaoTacs(): boolean {
    return this.emissaoCompleta && this.showProcessTable;
  }

  get showAdminExtras(): boolean {
    return this.emissaoCompleta && this.data.situacao === 'possui_pendencias';
  }

  get podeMostrarBotaoEmitir(): boolean {
    if (!this.data.podeEmitir) {
      return false;
    }
    if (this.emissaoCompleta) {
      return !this.showSelecaoTacs || this.algumSelecionado;
    }
    return this.data.situacao === 'nada_consta';
  }

  get todosSelecionados(): boolean {
    return this.tacsUnificados.length > 0 &&
      this.tacsUnificados.every(p => this.tacsSelecionados.has(p.numero));
  }

  get algumSelecionado(): boolean {
    return this.tacsUnificados.some(p => this.tacsSelecionados.has(p.numero));
  }

  ngOnInit(): void {
    const tacs = this.data.tacs || [];
    this.tacsUnificados = tacs.map((t) => ({
      numero: t.numero_processo_sei,
      status: t.status || '-',
      dataCelebracao: this.formatarData(t.data_registro_tac),
      dataVigencia: this.formatarData(t.data_inicio_vigencia_tac),
    }));
    if (this.emissaoCompleta) {
      this.tacsUnificados.forEach(p => this.tacsSelecionados.add(p.numero));
    }
  }

  private formatarData(valor?: string | null): string {
    if (!valor) {
      return '-';
    }
    const parte = String(valor).trim().substring(0, 10);
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(parte);
    if (iso) {
      return `${iso[3]}/${iso[2]}/${iso[1]}`;
    }
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) {
      return valor;
    }
    return data.toLocaleDateString('pt-BR');
  }

  toggleTac(numero: string): void {
    if (this.tacsSelecionados.has(numero)) {
      this.tacsSelecionados.delete(numero);
    } else {
      this.tacsSelecionados.add(numero);
    }
  }

  toggleTodos(): void {
    if (this.todosSelecionados) {
      this.tacsSelecionados.clear();
    } else {
      this.tacsUnificados.forEach(p => this.tacsSelecionados.add(p.numero));
    }
  }

  emitirESair(): void {
    this.dialogRef.close({
      emitir: true,
      tacsSelecionados: [...this.tacsSelecionados],
      observacoes: this.observacoes ? this.observacoes.trim() : undefined
    });
  }

  emitirNadaConsta(): void {
    this.dialogRef.close({
      emitir: true,
      forcarNadaConsta: true,
      tacsSelecionados: [...this.tacsSelecionados],
      observacoes: this.observacoes ? this.observacoes.trim() : undefined
    });
  }

  sair(): void {
    this.dialogRef.close({ emitir: false });
  }
}
